import type { CSSProperties, ReactElement } from 'react';
import { useReducedMotion } from 'motion/react';
import induce_logo from '../../assets/induce_logo.svg';

type Module = {
  name: string;
  /** Short form used on the loop-coverage chart. */
  short: string;
  desc: string;
  role: string;
  icon: ReactElement;
  /** Watermark diagram bled off the right edge of the row card. */
  glyph: ReactElement;
};

const MODULES: Module[] = [
  {
    name: 'Project Memory Graph',
    short: 'Memory Graph',
    desc: 'Who exists, what’s true, and when.',
    role: 'System of record',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 7.5v9M6 7.5l12-2.5M6 16.5l12 2.5M18 5v14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="6" cy="7.5" r="2.1" stroke="currentColor" strokeWidth="1.3" />
        <circle cx="6" cy="16.5" r="2.1" stroke="currentColor" strokeWidth="1.3" />
        <circle cx="18" cy="12" r="2.1" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    ),
    // bitemporal: two rails (story time / revision time) cross-linked
    glyph: (
      <svg viewBox="0 0 130 44" fill="none" aria-hidden="true">
        <path d="M2 13h126M2 33h126" stroke="currentColor" strokeWidth="1" strokeDasharray="2 5" />
        <path d="M28 13 52 33M66 13 92 33M92 13l-26 20" stroke="currentColor" strokeWidth="1" />
        <circle cx="28" cy="13" r="3" stroke="currentColor" strokeWidth="1.1" />
        <circle cx="66" cy="13" r="3" stroke="currentColor" strokeWidth="1.1" />
        <circle cx="92" cy="13" r="3" stroke="currentColor" strokeWidth="1.1" />
        <circle cx="52" cy="33" r="3" stroke="currentColor" strokeWidth="1.1" />
        <circle cx="92" cy="33" r="3" stroke="currentColor" strokeWidth="1.1" />
        <circle cx="66" cy="33" r="3" stroke="currentColor" strokeWidth="1.1" />
      </svg>
    ),
  },
  {
    name: 'Coherence Verifier',
    short: 'Verifier',
    desc: 'Every candidate graded before it ships.',
    role: 'Correctness',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3.2 19.2 6v6c0 4.2-2.9 7.4-7.2 8.8C7.7 19.4 4.8 16.2 4.8 12V6L12 3.2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="m8.9 12.1 2.2 2.2 4-4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    // candidates scored against a pass threshold
    glyph: (
      <svg viewBox="0 0 130 44" fill="none" aria-hidden="true">
        <path d="M2 16h126" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
        {[
          [14, 24],
          [32, 10],
          [50, 30],
          [68, 8],
          [86, 20],
          [104, 6],
        ].map(([x, h]) => (
          <path key={x} d={`M${x} 38V${38 - h}`} stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        ))}
        <path d="m116 15 4 4 8-9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    name: 'Principle Mapper',
    short: 'Mapper',
    desc: 'Director-corrected taste, not generic defaults.',
    role: 'Taste',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.3" />
        <path d="M12 3.6 7.4 11.6M20.4 12h-9.2M16.6 19.2 12 11.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="12" cy="12" r="2.3" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    ),
    // a hand-tuned curve with its control handles showing
    glyph: (
      <svg viewBox="0 0 130 44" fill="none" aria-hidden="true">
        <path d="M4 36C26 36 34 10 58 10s34 20 68 4" stroke="currentColor" strokeWidth="1.5" />
        <path d="M22 36v-8M58 10v10M96 22V12" stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" />
        <circle cx="22" cy="26" r="2.6" fill="currentColor" />
        <circle cx="58" cy="22" r="2.6" fill="currentColor" />
        <circle cx="96" cy="10" r="2.6" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: 'Shot Spec IR',
    short: 'Shot Spec',
    desc: 'One grammar. Any frontier generator.',
    role: 'Portability',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.4" y="3.4" width="7.2" height="7.2" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
        <rect x="3.4" y="13.4" width="7.2" height="7.2" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
        <rect x="13.4" y="13.4" width="7.2" height="7.2" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
        <path d="M14 7h6.6M17.3 3.7v6.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
    // one spec fanning out into interchangeable generator sockets
    glyph: (
      <svg viewBox="0 0 130 44" fill="none" aria-hidden="true">
        <rect x="3" y="14" width="26" height="16" rx="3" stroke="currentColor" strokeWidth="1.2" />
        <path d="M29 22h20M49 22V8h16M49 22v14h16M49 22h16" stroke="currentColor" strokeWidth="1" />
        <rect x="66" y="2" width="22" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
        <rect x="66" y="16" width="22" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
        <rect x="66" y="30" width="22" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
        <path d="M96 8h10M96 22h10M96 36h10" stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" />
      </svg>
    ),
  },
];

/**
 * Where each layer acts across the six stages of the loop
 * (Plan · Generate · Verify · Diagnose · Re-plan · Approve — see RhapsodyLoop).
 * Index order matches MODULES.
 */
const LOOP_STAGES = 6;
const LAYER_SPANS: [number, number][][] = [
  [[0, 6]], // memory graph — read and written at every stage
  [[2, 4]], // verifier — verify, diagnose
  [
    [0, 1],
    [3, 5],
  ], // mapper — planning and diagnosis
  [
    [0, 2],
    [4, 5],
  ], // shot spec — plan→generate, then re-plan
];

/** Node markers scattered across the stat plate. */
const STAT_NODES = [
  { x: 6, y: 34 },
  { x: 17, y: 55 },
  { x: 62, y: 44 },
  { x: 86, y: 26 },
];

/* ------------------------------------------------------------------
   Verification field — tilted elliptical orbits around the Verifier.
   Every shot travels all three axes before it can reach the cut.
   ------------------------------------------------------------------ */
const CX = 200;
const CY = 200;

/** Ellipse as a path, so <animateMotion path> can walk it directly. */
const ellipsePath = (rx: number, ry: number) =>
  `M${CX - rx},${CY}a${rx},${ry} 0 1,0 ${rx * 2},0a${rx},${ry} 0 1,0 ${-rx * 2},0`;

const pointOn = (rx: number, ry: number, deg: number) => {
  const t = (deg * Math.PI) / 180;
  return { x: CX + rx * Math.cos(t), y: CY + ry * Math.sin(t) };
};

/**
 * Labels are anchored at each ellipse's major-axis extreme and pushed a little
 * further out on a leader line — the one place on an orbit guaranteed to clear
 * both the core and the neighbouring planes.
 */
const ORBITS = [
  { rx: 92, ry: 56, tilt: -22, dur: 15, live: true, label: 'CAUSALITY', labelT: 180, pad: 34 },
  { rx: 132, ry: 86, tilt: 22, dur: 25, live: false, label: 'CONTINUITY', labelT: 0, pad: 32 },
  { rx: 170, ry: 114, tilt: -62, dur: 38, live: false, label: 'CRAFT', labelT: 0, pad: 30 },
  { rx: 193, ry: 150, tilt: 34, dur: 54, live: false, label: null, labelT: 0, pad: 0 },
];

/** Point on a tilted ellipse, in final (un-rotated) stage coordinates. */
const pointOnTilted = (rx: number, ry: number, deg: number, tilt: number) => {
  const t = (deg * Math.PI) / 180;
  const th = (tilt * Math.PI) / 180;
  const x = rx * Math.cos(t);
  const y = ry * Math.sin(t);
  return {
    x: CX + x * Math.cos(th) - y * Math.sin(th),
    y: CY + x * Math.sin(th) + y * Math.cos(th),
  };
};

/** Shots riding each orbit: `pass` has cleared that axis, `hold` has not yet. */
const ORBIT_MARKS: { o: number; t: number; kind: 'pass' | 'hold' }[] = [
  { o: 0, t: 128, kind: 'pass' },
  { o: 1, t: 34, kind: 'hold' },
  { o: 1, t: 250, kind: 'pass' },
  { o: 2, t: 8, kind: 'hold' },
  { o: 2, t: 196, kind: 'pass' },
  { o: 3, t: 68, kind: 'hold' },
  { o: 3, t: 286, kind: 'hold' },
];

export function RhapsodyModules() {
  const reduce = useReducedMotion();

  return (
    <section className="rh-section" id="layers">
      <div className="rh-modules">
        <p className="rh-kicker">One engine, four layers</p>
        <h2 className="rh-h2">
          Everything above the generators. <em>Nothing left to chance.</em>
        </h2>

        <div className="rh-bento">
          {/* ---------- Left column: 01 · 02 · the stat plate ---------- */}
          <div className="rh-bento-col">
            {MODULES.slice(0, 2).map((m, i) => (
              <ModuleCard key={m.name} module={m} index={i} />
            ))}

            <article className="rh-bento-card rh-bento-hero rh-bento-stat">
              <div className="rh-stat-field" aria-hidden="true">
                <span className="rh-field-rules" />
                <svg className="rh-spark" viewBox="0 0 480 300" preserveAspectRatio="none">
                  <path
                    d="M0 262C48 262 78 256 110 240c34-17 54-40 88-50 33-10 56 6 88-4 34-11 54-42 92-60 30-14 74-20 102-20"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
                {STAT_NODES.map((n) => (
                  <i
                    key={`${n.x}-${n.y}`}
                    className="rh-node"
                    style={{ left: `${n.x}%`, top: `${n.y}%` }}
                  />
                ))}
                <div className="rh-numeral">
                  <span className="ghost g3">04</span>
                  <span className="ghost g2">04</span>
                  <span className="ghost g1">04</span>
                  <span className="lead">04</span>
                </div>
              </div>

              <LoopCoverage />

              <div className="rh-bento-caption">
                <h3>Four layers, one engine</h3>
                <p>Reasoning, memory, taste and verification — above every generator.</p>
              </div>
            </article>
          </div>

          {/* ---------- Right column: the orbit plate · 03 · 04 ---------- */}
          <div className="rh-bento-col">
            <article className="rh-bento-card rh-bento-hero rh-bento-orbit">
              <div className="rh-bento-caption top">
                <h3>Zero unverified shots</h3>
                <p>Every shot clears causality, continuity and craft before the cut.</p>
              </div>
              <VerificationField reduce={!!reduce} />
            </article>

            {MODULES.slice(2).map((m, i) => (
              <ModuleCard key={m.name} module={m} index={i + 2} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** The four layers charted against the six stages of the loop. */
function LoopCoverage() {
  return (
    <div className="rh-coverage" aria-hidden="true">
      <p className="rh-coverage-head">Where each layer acts</p>

      <div className="rh-coverage-rows">
        {MODULES.map((m, i) => (
          <div className="rh-cov-row" key={m.name} style={{ '--i': i } as CSSProperties}>
            <span className="ix">{String(i + 1).padStart(2, '0')}</span>
            <span className="lb">{m.short}</span>
            <span className="track">
              {LAYER_SPANS[i].map(([from, to]) => (
                <i
                  key={`${from}-${to}`}
                  className="seg"
                  style={{
                    left: `${(from / LOOP_STAGES) * 100}%`,
                    width: `${((to - from) / LOOP_STAGES) * 100}%`,
                  }}
                />
              ))}
            </span>
          </div>
        ))}
      </div>

      {/* same grid as the rows above, so the axis sits under the tracks */}
      <div className="rh-coverage-axis">
        <span />
        <span />
        <span className="rail">
          <span>Plan</span>
          <span className="ticks">
            {Array.from({ length: LOOP_STAGES - 1 }, (_, i) => (
              <i key={i} />
            ))}
          </span>
          <span>Approve</span>
        </span>
      </div>
    </div>
  );
}

function VerificationField({ reduce }: { reduce: boolean }) {
  return (
    <div className="rh-orbit-field" aria-hidden="true">
      <div className="rh-orbit-stage">
        <span className="rh-orbit-sweep" />

        {/* 400 user units == 10em, so the diagram scales off the stage font-size */}
        <svg className="rh-orbit-svg" viewBox="0 0 400 400">
          {ORBITS.map((o, oi) => {
            const d = ellipsePath(o.rx, o.ry);
            const marks = ORBIT_MARKS.filter((m) => m.o === oi);
            const parked = pointOn(o.rx, o.ry, 42);

            return (
              <g key={o.label ?? oi} transform={`rotate(${o.tilt} ${CX} ${CY})`}>
                <path className={`orb${o.live ? ' live' : ''}`} d={d} />

                {marks.map((m) => {
                  const p = pointOn(o.rx, o.ry, m.t);
                  return m.kind === 'pass' ? (
                    <circle key={m.t} className="mk pass" cx={p.x} cy={p.y} r="3.6" />
                  ) : (
                    <rect
                      key={m.t}
                      className="mk hold"
                      x="-3.4"
                      y="-3.4"
                      width="6.8"
                      height="6.8"
                      transform={`translate(${p.x} ${p.y}) rotate(${45 - o.tilt})`}
                    />
                  );
                })}

                {/* the shot currently travelling this axis */}
                <g className={`tracer${o.live ? ' live' : ''}`}>
                  <circle cx={reduce ? parked.x : 0} cy={reduce ? parked.y : 0} r={o.live ? 5 : 3.2} />
                  {!reduce && (
                    <animateMotion dur={`${o.dur}s`} repeatCount="indefinite" begin={`-${o.dur * 0.37}s`} path={d} />
                  )}
                </g>
              </g>
            );
          })}

          {/* axis labels live outside the rotated groups so they stay upright */}
          {ORBITS.filter((o) => o.label).map((o) => {
            const anchor = pointOnTilted(o.rx, o.ry, o.labelT, o.tilt);
            const at = pointOnTilted(o.rx + o.pad, o.ry + o.pad, o.labelT, o.tilt);
            return (
              <g key={o.label} className="orb-tag">
                <line x1={anchor.x} y1={anchor.y} x2={at.x} y2={at.y} />
                <text x={at.x} y={at.y} dy={at.y < CY ? -9 : 13}>
                  {o.label}
                </text>
              </g>
            );
          })}
        </svg>

        <span className="rh-orbit-core">
          <img src={induce_logo} alt="Induce" className="rh-orbit-core-logo" />
        </span>
      </div>

      <p className="rh-orbit-legend">
        <span className="pass">Cleared</span>
        <span className="hold">In review</span>
      </p>
    </div>
  );
}

function ModuleCard({ module: m, index }: { module: Module; index: number }) {
  return (
    <article className="rh-bento-card rh-bento-row">
      <span className="rh-row-mark" aria-hidden="true">
        {m.icon}
      </span>
      <span className="rh-row-head">
        <span className="rh-row-name">
          <span className="idx" aria-hidden="true">
            {String(index + 1).padStart(2, '0')}
          </span>
          {m.name}
        </span>
        <span className="rh-row-role">{m.role}</span>
      </span>
      <span className="rh-row-desc">{m.desc}</span>
      <span className="rh-row-glyph" aria-hidden="true">
        {m.glyph}
      </span>
    </article>
  );
}
